// ---------------------------------------------------------------------------
// 언어(locale) 선호값의 단일 소스. apps/app/src/lib/theme.ts 의 localStorage +
// useSyncExternalStore 패턴을 미러링한다. 저장된 값이 없으면 브라우저 언어를
// 감지해 fallback 한다. 서버(user_preference.locale) 동기화는
// apps/app/src/queries/user-preferences/query.ts 참고.
// ---------------------------------------------------------------------------

import { useSyncExternalStore } from "react";
import { DEFAULT_LOCALE, isSupportedLocale, type SupportedLocale } from "./locales";

export const LOCALE_STORAGE_KEY = "locale";

function readStorage(): string | null {
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    // localStorage 접근 불가(사생활 보호 모드 등)면 기본값으로 동작
    return null;
  }
}

function writeStorage(value: string) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, value);
  } catch {
    // 저장 실패해도 이번 세션 동안은 적용은 유지
  }
}

// navigator.language(단일 값)만 보면 목록이 ["fr-FR", "ko-KR"] 인 사용자가 ko 를
// 쓸 수 있는데도 기본값(en)으로 떨어진다. navigator.languages 를 선호 순서대로
// 훑어 지원하는 첫 언어를 쓴다. (구형 브라우저 대비로 navigator.language 폴백)
function detectBrowserLocale(): SupportedLocale {
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const language of preferred) {
    const base = language.slice(0, 2);
    if (isSupportedLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

export function getLocalePreference(): SupportedLocale {
  const stored = readStorage();
  return isSupportedLocale(stored) ? stored : detectBrowserLocale();
}

const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

/** 언어 선호값을 저장하고 구독자에게 알린다. 서버 저장은 호출부 책임. */
export function setLocalePreference(locale: SupportedLocale) {
  writeStorage(locale);
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** 설정 UI 등에서 현재 언어 선호값을 구독한다. */
export function useLocalePreference(): SupportedLocale {
  return useSyncExternalStore(subscribe, getLocalePreference);
}

// 같은 브라우저의 다른 탭에서 언어를 바꾼 경우 따라간다.
// (서버/테스트 등 브라우저가 아닌 환경에서 import 될 수 있으므로 가드한다.)
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    // 언어 키가 아닌지
    const isUnrelatedKey = event.key !== LOCALE_STORAGE_KEY;
    if (isUnrelatedKey) return;
    notify();
  });
}
